"use client";

import { c as _c } from "react/compiler-runtime";
import { IconDownload2Outline18, IconGasPumpOutline18 } from "nucleo-ui-outline-18";
import { Button } from "@/components/ui/button";
import type { NodeGasStatus } from "@/hooks/useNodeGasStatus";
import { getChainInfo } from "@/lib/chains";
import { isValidBlockchainAddress } from "@/hooks/useAddressValidation";
import { isNativeTokenAddress } from "@/lib/tokens/nativeToken";
import { useModalStore } from "@b3dotfun/sdk/global-account/react";
import { NodeToolbar } from "@xyflow/react";
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { isAddress } from "viem";
import { useNodeToolbarPlacement } from "./useNodeToolbarPlacement";
const GAS_TOOLBAR_OFFSET = 20;

/** Grace period before hiding so the user can move their mouse into the toolbar. */
const HIDE_DELAY_MS = 400;
interface NodeGasWarningToolbarProps {
  gasStatus: NodeGasStatus;
  isVisible: boolean;
  nodeRef?: RefObject<HTMLDivElement | null>;
}

/**
 * NodeGasWarningToolbar - Shows gas/token warning above a node.
 * Shows deposit buttons for missing gas and/or required tokens.
 */
export function NodeGasWarningToolbar(t0) {
  const $ = _c(46);
  const {
    gasStatus,
    isVisible: parentVisible,
    nodeRef
  } = t0;
  const [isToolbarHovered, setIsToolbarHovered] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const hideTimeoutRef = useRef(undefined);
  const setModalOpen = useModalStore(_temp);
  const setModalContentType = useModalStore(_temp2);
  let t1;
  if ($[0] !== nodeRef || $[1] !== showToolbar) {
    t1 = {
      isVisible: showToolbar,
      nodeRef,
      offset: GAS_TOOLBAR_OFFSET
    };
    $[0] = nodeRef;
    $[1] = showToolbar;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    bridgeClassName,
    enterAnimationClassName,
    position,
    toolbarRef
  } = useNodeToolbarPlacement(t1);
  let t2;
  if ($[3] !== gasStatus.walletAddress || $[4] !== setModalContentType || $[5] !== setModalOpen) {
    t2 = (e, tokenAddress, chainId, symbol) => {
      e.stopPropagation();
      if (!gasStatus.walletAddress || !isValidBlockchainAddress(gasStatus.walletAddress)) {
        return;
      }
      setModalContentType({
        type: "anySpendDeposit",
        recipientAddress: gasStatus.walletAddress,
        destinationTokenAddress: tokenAddress,
        destinationTokenChainId: chainId,
        actionLabel: `deposit ${symbol} on ${getChainInfo(chainId).name}`,
        allowDirectTransfer: true,
        showFiatOption: false
      });
      setModalOpen(true);
    };
    $[3] = gasStatus.walletAddress;
    $[4] = setModalContentType;
    $[5] = setModalOpen;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const openDeposit = t2;
  let t3;
  let t4;
  if ($[7] !== isToolbarHovered || $[8] !== parentVisible) {
    t3 = () => {
      if (parentVisible || isToolbarHovered) {
        clearTimeout(hideTimeoutRef.current);
        setShowToolbar(true);
      } else {
        hideTimeoutRef.current = setTimeout(() => setShowToolbar(false), HIDE_DELAY_MS);
      }
      return () => clearTimeout(hideTimeoutRef.current);
    };
    t4 = [parentVisible, isToolbarHovered];
    $[7] = isToolbarHovered;
    $[8] = parentVisible;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  const needsGas = gasStatus.needsGasWarning;
  const needsTokens = gasStatus.needsTokenWarning;
  if (!needsGas && !needsTokens || !showToolbar) {
    return null;
  }
  const chainName = gasStatus.chainInfo?.name || (gasStatus.chainId ? `Chain ${gasStatus.chainId}` : "the target chain");
  const gasToken = gasStatus.chainInfo?.gasToken || "ETH";
  let depositRows;
  if ($[11] !== gasStatus.chainId || $[12] !== gasStatus.missingTokens || $[13] !== gasToken || $[14] !== needsGas) {
    depositRows = [];
    if (needsGas && gasStatus.chainId !== undefined) {
      let t5;
      if ($[16] !== gasStatus.chainId || $[17] !== gasToken) {
        t5 = {
          key: "gas",
          address: "0x0000000000000000000000000000000000000000",
          chainId: gasStatus.chainId,
          symbol: gasToken,
          reason: "for gas fees"
        };
        $[16] = gasStatus.chainId;
        $[17] = gasToken;
        $[18] = t5;
      } else {
        t5 = $[18];
      }
      depositRows.push(t5);
    }
    for (const token of gasStatus.missingTokens) {
      if (needsGas && token.chainId === gasStatus.chainId && isNativeTokenAddress(token.address)) {
        continue;
      }
      depositRows.push({
        key: `${token.chainId}-${token.address}`,
        address: token.address,
        chainId: token.chainId,
        symbol: token.symbol,
        reason: token.reason
      });
    }
    $[11] = gasStatus.chainId;
    $[12] = gasStatus.missingTokens;
    $[13] = gasToken;
    $[14] = needsGas;
    $[15] = depositRows;
  } else {
    depositRows = $[15];
  }
  const title = needsGas ? "No gas balance" : "Missing token balance";
  const description = needsGas ? `Requires ${gasToken} on ${chainName} for gas fees.` : `Requires tokens you don't have yet.`;
  let t5;
  let t6;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setIsToolbarHovered(true);
    t6 = () => setIsToolbarHovered(false);
    $[19] = t5;
    $[20] = t6;
  } else {
    t5 = $[19];
    t6 = $[20];
  }
  let t7;
  if ($[21] !== bridgeClassName) {
    t7 = <div className={bridgeClassName} />;
    $[21] = bridgeClassName;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const t8 = `animate-in fade-in ${enterAnimationClassName} shadow-ds-elev-2 w-full max-w-[320px] rounded-xl border border-amber-300 bg-amber-50/90 p-3 px-4 backdrop-blur-md duration-200 dark:border-amber-800 dark:bg-amber-950/80`;
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <IconGasPumpOutline18 className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== title) {
    t10 = <p className="label label-xs-caps text-amber-700 dark:text-amber-300">{title}</p>;
    $[24] = title;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== description) {
    t11 = <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{description}</p>;
    $[26] = description;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t10 || $[29] !== t11) {
    t12 = <div className="flex items-start gap-2">{t9}<div className="min-w-0 flex-1">{t10}{t11}</div></div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== depositRows || $[32] !== openDeposit) {
    t13 = <div className="mt-4">{depositRows.map(row => <Button key={row.key} onClick={e_0 => openDeposit(e_0, row.address, row.chainId, row.symbol)} variant="white" size="sm" className="w-full"><span className="label-xs label flex items-center gap-1 text-amber-700 dark:text-amber-400"><IconDownload2Outline18 className="h-3 w-3" />Deposit or swap for {row.symbol}</span></Button>)}</div>;
    $[31] = depositRows;
    $[32] = openDeposit;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t12 || $[35] !== t13 || $[36] !== t8) {
    t14 = <div className={t8}>{t12}{t13}</div>;
    $[34] = t12;
    $[35] = t13;
    $[36] = t8;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t14 || $[39] !== t7 || $[40] !== toolbarRef) {
    t15 = <div ref={toolbarRef} className="relative" onMouseEnter={t5} onMouseLeave={t6}>{t7}{t14}</div>;
    $[38] = t14;
    $[39] = t7;
    $[40] = toolbarRef;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== position || $[43] !== showToolbar || $[44] !== t15) {
    t16 = <NodeToolbar isVisible={showToolbar} position={position} align="center" offset={GAS_TOOLBAR_OFFSET}>{t15}</NodeToolbar>;
    $[42] = position;
    $[43] = showToolbar;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  return t16;
}
function _temp2(state_0) {
  return state_0.setB3ModalContentType;
}
function _temp(state) {
  return state.setB3ModalOpen;
}